export function buildWeeklyCallStarts(
  localStart: string,
  intervalWeeks: number,
  occurrenceCount: number
): string[] {
  if (!Number.isInteger(intervalWeeks) || intervalWeeks < 1 || intervalWeeks > 8) {
    throw new Error("invalid_interval");
  }
  if (!Number.isInteger(occurrenceCount) || occurrenceCount < 1 || occurrenceCount > 16) {
    throw new Error("invalid_occurrence_count");
  }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localStart)) {
    throw new Error("invalid_start");
  }
  const first = new Date(localStart);
  if (!Number.isFinite(first.getTime())) throw new Error("invalid_start");

  return Array.from({ length: occurrenceCount }, (_, index) => {
    const occurrence = new Date(first);
    occurrence.setDate(first.getDate() + index * intervalWeeks * 7);
    return occurrence.toISOString();
  });
}

export interface AdminCallStartParts {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
}

/**
 * Náhled termínů je jediné místo pravdy o tom, jestli se dá vypsat: kreslí ho
 * panel a stejný stav zamyká tlačítko. Dřív každé odmítnutí spadlo do prázdného
 * pole a panel pak nad 31. únorem tvrdil "Zvol datum a čas" - tedy popisoval
 * jiný stav, než ve kterém uživatel byl, a tlačítko jen zšedlo bez důvodu.
 * Proto vrací DŮVOD, ne jen seznam.
 */
export type AdminCallPreview =
  | { status: "incomplete" }
  | { status: "invalid_date" }
  | { status: "past"; suggestion: AdminCallStartParts }
  | { status: "missing_url" }
  | { status: "invalid_series" }
  | { status: "ready"; starts: string[] };

/** Sestaví `YYYY-MM-DDTHH:MM`, nebo prázdný řetězec, když datum neexistuje. */
export function adminCallStartValue(parts: AdminCallStartParts): string {
  if (Object.values(parts).some((part) => !part)) return "";
  const candidate = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  const parsed = new Date(candidate);
  if (!Number.isFinite(parsed.getTime()) || parsed.getFullYear() !== Number(parts.year) ||
      parsed.getMonth() + 1 !== Number(parts.month) || parsed.getDate() !== Number(parts.day)) return "";
  return candidate;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Nejbližší celá hodina po `now` - deterministická nabídka, ne hádání. */
export function nextWholeHourParts(now: number): AdminCallStartParts {
  const at = new Date(now);
  at.setMinutes(0, 0, 0);
  at.setHours(at.getHours() + 1);
  return {
    day: pad(at.getDate()),
    month: pad(at.getMonth() + 1),
    year: String(at.getFullYear()),
    hour: pad(at.getHours()),
    minute: "00"
  };
}

/**
 * S2-11: náhled má být JEDINÉ místo, kde se dozvíš, jestli termín půjde
 * vypsat, jenže jedna blokující podmínka v něm nebyla. S platným datem a
 * prázdným polem "URL hovoru" svítil náhled zeleně, tlačítko bylo aktivní a
 * odeslání odmítla až nativní bublina prohlížeče, anglicky a mimo panel.
 * Podmínka, která rozhoduje o odeslání, patří do náhledu.
 */
export function adminCallPreview(
  parts: AdminCallStartParts,
  options: {
    repeat: boolean;
    intervalWeeks: number;
    occurrenceCount: number;
    now: number;
    /** Volitelné jen kvůli starším voláním; formulář ho předává vždy. */
    zoomUrl?: string;
  }
): AdminCallPreview {
  if (Object.values(parts).some((part) => !part)) return { status: "incomplete" };
  const start = adminCallStartValue(parts);
  if (!start) return { status: "invalid_date" };
  if (Date.parse(start) <= options.now) {
    return { status: "past", suggestion: nextWholeHourParts(options.now) };
  }
  if (options.zoomUrl !== undefined && !options.zoomUrl.trim()) return { status: "missing_url" };
  try {
    return {
      status: "ready",
      starts: buildWeeklyCallStarts(
        start,
        options.repeat ? options.intervalWeeks : 1,
        options.repeat ? options.occurrenceCount : 1
      )
    };
  } catch {
    return { status: "invalid_series" };
  }
}
